import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { fmtINR, formatApiError } from "@/lib/api";
import { StatusBadge } from "@/pages/Dashboard";
import { Search, Mail, Loader2 } from "lucide-react";
import { toast } from "sonner";

const FILTERS = [
  { key: "all", label: "ALL" },
  { key: "preorder", label: "PRE-ORDER" },
  { key: "awaiting_address", label: "AWAITING ADDR" },
  { key: "address_received", label: "READY" },
  { key: "packed", label: "PACKED" },
  { key: "shipped", label: "SHIPPED" },
  { key: "delivered", label: "DELIVERED" },
];

export default function Orders() {
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [resendingId, setResendingId] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/api/orders");
      setOrders(data);
    })();
  }, []);

  const resendConfirmation = async (e, order) => {
    e.stopPropagation();
    if (!order?.customer_snapshot?.email) {
      toast.error("No customer email on this order");
      return;
    }
    setResendingId(order.id);
    try {
      const { data } = await api.post(`/api/orders/${order.id}/resend-email`, null, {
        params: { kind: "confirmation" },
      });
      toast.success(`Confirmation emailed to ${data.to}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setResendingId(null);
    }
  };

  const visible = orders
    .filter((o) => filter === "all" || o.status === filter)
    .filter((o) => {
      if (!q.trim()) return true;
      const t = q.toLowerCase();
      return (
        o.order_number.toLowerCase().includes(t) ||
        o.customer_snapshot?.name?.toLowerCase().includes(t) ||
        o.customer_snapshot?.phone?.includes(t) ||
        o.awb_number?.toLowerCase().includes(t)
      );
    });

  return (
    <div className="p-4 sm:p-6 lg:p-12">
      <div className="mb-8">
        <div className="font-mono text-xs tracking-[0.3em] text-[color:var(--mecha-red)]">
          // ORDER LOG
        </div>
        <h1 className="font-display text-3xl sm:text-5xl mt-3 leading-none">ORDERS</h1>
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            data-testid={`filter-${f.key}`}
            onClick={() => setFilter(f.key)}
            className={`h-10 px-4 text-xs font-mono tracking-widest border transition-colors ${
              filter === f.key
                ? "bg-white text-black border-white"
                : "border-zinc-800 text-zinc-400 hover:border-white hover:text-white"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="relative mb-6">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
        <input
          data-testid="orders-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search order #, customer name, phone, AWB..."
          className="w-full h-12 bg-zinc-950 border border-zinc-800 pl-12 pr-4 text-white placeholder-zinc-600 focus:outline-none focus:border-[color:var(--mecha-red)]"
        />
      </div>

      <div className="card-brutal overflow-x-auto">
        {visible.length === 0 ? (
          <div className="p-12 text-center text-zinc-500 text-sm">
            No orders match this view.
          </div>
        ) : (
          <table className="w-full min-w-[980px]">
            <thead>
              <tr className="border-b border-zinc-800">
                {["ORDER", "DATE", "CUSTOMER", "ITEMS", "STATUS", "AWB", "TOTAL", ""].map(
                  (h) => (
                    <th
                      key={h || "actions"}
                      className="text-left font-mono text-[10px] tracking-[0.3em] text-zinc-500 p-4"
                    >
                      {h}
                    </th>
                  )
                )}
              </tr>
            </thead>
            <tbody>
              {visible.map((o) => (
                <tr
                  key={o.id}
                  data-testid={`order-row-${o.order_number}`}
                  onClick={() => nav(`/orders/${o.id}`)}
                  className="border-b border-zinc-900 last:border-0 hover:bg-zinc-900/50 cursor-pointer"
                >
                  <td className="p-4 font-mono text-sm">{o.order_number}</td>
                  <td className="p-4 text-xs text-zinc-400 font-mono">
                    {new Date(o.created_at).toLocaleDateString("en-IN", {
                      day: "2-digit",
                      month: "short",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </td>
                  <td className="p-4 text-sm">
                    {o.customer_snapshot?.name || (
                      <span className="text-zinc-600">—</span>
                    )}
                    {o.customer_snapshot?.phone && (
                      <div className="font-mono text-[10px] text-zinc-500">
                        {o.customer_snapshot.phone}
                      </div>
                    )}
                  </td>
                  <td className="p-4 font-mono text-sm">
                    {o.items.reduce((s, i) => s + i.quantity, 0)}
                  </td>
                  <td className="p-4">
                    <StatusBadge status={o.status} />
                  </td>
                  <td className="p-4 font-mono text-xs text-zinc-400">
                    {o.awb_number || "—"}
                  </td>
                  <td className="p-4 text-right font-mono font-bold">
                    {fmtINR(o.total)}
                  </td>
                  <td className="p-4 text-right">
                    <button
                      type="button"
                      data-testid={`resend-email-${o.order_number}`}
                      title={
                        o.customer_snapshot?.email
                          ? `Resend confirmation to ${o.customer_snapshot.email}`
                          : "No customer email on file"
                      }
                      onClick={(e) => resendConfirmation(e, o)}
                      disabled={resendingId === o.id || !o.customer_snapshot?.email}
                      className="h-9 px-3 border border-zinc-700 hover:border-white text-[10px] font-mono tracking-widest uppercase inline-flex items-center gap-2 disabled:opacity-40 disabled:hover:border-zinc-700"
                    >
                      {resendingId === o.id ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Mail className="h-3.5 w-3.5" />
                      )}
                      Resend
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
